/**
 * Platform-agnostic HTTP client for the Nordex Rust backend API.
 * 
 * Works in both web browsers and React Native.
 */

import { getApiBaseUrl, getAuthTokenGetter } from "../config";
import type {
  Account,
  AccountResponse,
  ApiError,
  CreateOrderRequest,
  CreateWithdrawalRequest,
  CreateWithdrawalResponse,
  Deposit,
  TwoFactorInitResponse,
  TwoFactorVerifySetupResponse,
  TwoFactorDisableResponse,
  TwoFactorStatusResponse,
  TwoFactorRegenerateBackupResponse,
  DepositAddressResponse,
  DepositListResponse,
  DepositWithdrawRequest,
  DepositWithdrawResponse,
  FaucetResponse,
  FeesConfig,
  Fill,
  FundingPayment,
  FundingPaymentsResponse,
  FundingRate,
  FundingRatesResponse,
  GetTpSlTargetsResponse,
  MarketStats,
  MarketStatsResponse,
  Order,
  OrderBookSnapshot,
  OrderListResponse,
  OrderPreviewRequest,
  OrderPreviewResponse,
  OrderResponse,
  Position,
  PositionListResponse,
  PositionResponse,
  SavedWithdrawalAddress,
  SavedWithdrawalAddressesResponse,
  SaveWithdrawalAddressResponse,
  SetTpSlTargetsRequest,
  TpSlTarget,
  TpSlTargetDto,
  TpSlTargetsResponse,
  UpdateTpSlRequest,
  UpdateTpSlResponse,
  UpdateLeverageRequest,
  UpdateLeverageResponse,
  UpdatePositionMarginRequest,
  UpdatePositionMarginResponse,
  PreviewPositionMarginRequest,
  PreviewPositionMarginResponse,
  UpdateMarginModeRequest,
  UpdateMarginModeResponse,
  FavoriteMarketsResponse,
  VlpQuote,
  VlpQuoteResponse,
  VlpQuotesResponse,
  Withdrawal,
  WithdrawalListResponse,
} from "../types";

/**
 * Generic fetch wrapper with error handling and authentication
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${endpoint}`;

  // Get auth token if available
  const getAuthToken = getAuthTokenGetter();
  const authToken = getAuthToken ? await getAuthToken() : null;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };

  // Add Authorization header if token is available
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Try to parse error from response body
    let errorMessage: string;
    try {
      const errorData: ApiError = await response.json();
      errorMessage = errorData.error || `Request failed (${response.status})`;
    } catch {
      // Fallback if JSON parsing fails - handle empty statusText gracefully
      const statusText = response.statusText || 'Unknown error';
      errorMessage = `Request failed: ${statusText} (${response.status})`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Orders API
 */
export const OrdersApi = {
  /**
   * Place a new order
   * Returns both the order and optionally the position (for market orders)
   */
  async placeOrder(request: CreateOrderRequest): Promise<OrderResponse> {
    const response = await apiFetch<OrderResponse>("/api/orders", {
      method: "POST",
      body: JSON.stringify(request),
    });
    return response;
  },

  /**
   * Cancel an existing order
   */
  async cancelOrder(orderId: string): Promise<Order> {
    const response = await apiFetch<OrderResponse>(`/api/orders/${orderId}`, {
      method: "DELETE",
    });
    return response.order;
  },

  /**
   * List orders for the authenticated user
   */
  async listOrders(): Promise<Order[]> {
    const response = await apiFetch<OrderListResponse>(`/api/orders`);
    return response.orders;
  },

  /**
   * Preview order without execution
   * Industry standard: Backend calculates all risk metrics authoritatively
   * Used by Binance, Bybit, OKX for order form previews
   */
  async previewOrder(request: OrderPreviewRequest): Promise<OrderPreviewResponse> {
    return apiFetch<OrderPreviewResponse>("/api/orders/preview", {
      method: "POST",
      body: JSON.stringify(request),
    });
  },
};

/**
 * Positions API
 */
export const PositionsApi = {
  /**
   * List all positions for the authenticated user
   */
  async listPositions(): Promise<Position[]> {
    const response = await apiFetch<PositionListResponse>(`/api/positions`);
    return response.positions;
  },

  /**
   * Get a specific position for the authenticated user
   */
  async getPosition(market: string): Promise<Position> {
    const response = await apiFetch<PositionResponse>(
      `/api/positions/${encodeURIComponent(market)}`
    );
    return response.position;
  },

  /**
   * Update Take Profit and Stop Loss for a position
   */
  async updateTpSl(
    market: string,
    takeProfit?: number | null,
    stopLoss?: number | null
  ): Promise<UpdateTpSlResponse> {
    const request: UpdateTpSlRequest = {
      take_profit: takeProfit !== undefined && takeProfit !== null 
        ? takeProfit.toString() 
        : null,
      stop_loss: stopLoss !== undefined && stopLoss !== null 
        ? stopLoss.toString() 
        : null,
    };
    return apiFetch<UpdateTpSlResponse>(
      `/api/positions/${encodeURIComponent(market)}/tpsl`,
      {
        method: "PUT",
        body: JSON.stringify(request),
      }
    );
  },

  /**
   * Update leverage for an existing position
   */
  async updateLeverage(
    market: string,
    leverage: number
  ): Promise<UpdateLeverageResponse> {
    const request: UpdateLeverageRequest = {
      leverage: leverage.toString(),
    };
    return apiFetch<UpdateLeverageResponse>(
      `/api/positions/${encodeURIComponent(market)}/leverage`,
      {
        method: "PUT",
        body: JSON.stringify(request),
      }
    );
  },

  /**
   * Adjust position margin (isolated mode only)
   */
  async updatePositionMargin(
    market: string,
    marginDelta: number
  ): Promise<UpdatePositionMarginResponse> {
    const request: UpdatePositionMarginRequest = {
      margin_delta: marginDelta.toString(),
    };
    return apiFetch<UpdatePositionMarginResponse>(
      `/api/positions/${encodeURIComponent(market)}/margin`,
      {
        method: "PUT",
        body: JSON.stringify(request),
      }
    );
  },

  /**
   * Preview position margin adjustment (isolated mode only)
   * Shows new liquidation price without actually updating the position
   */
  async previewPositionMargin(
    market: string,
    marginDelta: number
  ): Promise<PreviewPositionMarginResponse> {
    const request: PreviewPositionMarginRequest = {
      margin_delta: marginDelta.toString(),
    };
    return apiFetch<PreviewPositionMarginResponse>(
      `/api/positions/${encodeURIComponent(market)}/margin/preview`,
      {
        method: "POST",
        body: JSON.stringify(request),
      }
    );
  },

  /**
   * Set partial TP/SL targets for a position (Partial mode)
   * Replaces any existing targets
   */
  async setTpSlTargets(
    market: string,
    targets: TpSlTarget[]
  ): Promise<TpSlTargetsResponse> {
    const request: SetTpSlTargetsRequest = {
      targets,
    };
    return apiFetch<TpSlTargetsResponse>(
      `/api/positions/${encodeURIComponent(market)}/tpsl/targets`,
      {
        method: "POST",
        body: JSON.stringify(request),
      }
    );
  },

  /**
   * Get current TP/SL targets for a position
   */
  async getTpSlTargets(
    market: string
  ): Promise<TpSlTargetDto[]> {
    const response = await apiFetch<GetTpSlTargetsResponse>(
      `/api/positions/${encodeURIComponent(market)}/tpsl/targets`
    );
    return response.targets;
  },

  /**
   * Clear all TP/SL targets and switch back to standard mode
   */
  async clearTpSlTargets(
    market: string
  ): Promise<void> {
    await apiFetch(
      `/api/positions/${encodeURIComponent(market)}/tpsl/targets`,
      {
        method: "DELETE",
      }
    );
  },
};

/**
 * Accounts API
 */
export const AccountsApi = {
  /**
   * Get account summary with calculated fields for authenticated user
   */
  async getAccount(): Promise<AccountResponse> {
    return apiFetch<AccountResponse>(`/api/accounts`);
  },

  /**
   * Create a new account if one doesn't already exist.
   * Returns the existing account if it already exists (idempotent).
   * Balances are server-authoritative -- the client cannot set collateral.
   */
  async createAccount(): Promise<Account> {
    const response = await apiFetch<AccountResponse>("/api/accounts", {
      method: "POST",
    });
    return response.account;
  },

  /**
   * Deposit funds into authenticated user's account
   */
  async deposit(amount: number): Promise<DepositWithdrawResponse> {
    const request: DepositWithdrawRequest = { amount: amount.toString() };
    return apiFetch<DepositWithdrawResponse>(
      `/api/accounts/deposit`,
      {
        method: "POST",
        body: JSON.stringify(request),
      }
    );
  },

  /**
   * Withdraw funds from authenticated user's account
   */
  async withdraw(amount: number): Promise<DepositWithdrawResponse> {
    const request: DepositWithdrawRequest = { amount: amount.toString() };
    return apiFetch<DepositWithdrawResponse>(
      `/api/accounts/withdraw`,
      {
        method: "POST",
        body: JSON.stringify(request),
      }
    );
  },

  /**
   * Update authenticated user's account margin mode (isolated vs cross)
   */
  async updateMarginMode(
    marginMode: "isolated" | "cross"
  ): Promise<UpdateMarginModeResponse> {
    const request: UpdateMarginModeRequest = {
      margin_mode: marginMode,
    };
    return apiFetch<UpdateMarginModeResponse>(
      `/api/accounts/margin-mode`,
      {
        method: "PUT",
        body: JSON.stringify(request),
      }
    );
  },
};

/**
 * Markets API
 */
export const MarketsApi = {
  /**
   * Get orderbook snapshot for a market
   */
  async getOrderbook(market: string, depth = 50): Promise<OrderBookSnapshot> {
    return apiFetch<OrderBookSnapshot>(
      `/api/markets/${encodeURIComponent(market)}/orderbook?depth=${depth}`
    );
  },
};

/**
 * VLP (Virtual Liquidity Pool) API
 */
export const VlpApi = {
  /**
   * Get all VLP quotes (bid/ask prices for all markets)
   */
  async getQuotes(): Promise<VlpQuote[]> {
    const response = await apiFetch<VlpQuotesResponse>("/api/vlp/quotes");
    return response.quotes;
  },

  /**
   * Get VLP quote for a specific market
   */
  async getQuote(market: string): Promise<VlpQuote> {
    const response = await apiFetch<VlpQuoteResponse>(
      `/api/vlp/quotes/${encodeURIComponent(market)}`
    );
    return response.quote;
  },
};

/**
 * Paginated fills response
 */
export interface PaginatedFillsResponse {
  fills: Fill[];
  hasMore: boolean;
}

/**
 * Fills API (Trade History)
 */
export const FillsApi = {
  /**
   * List fills (trade executions) for authenticated user with pagination
   * @param limit Maximum number of fills to return (default: 50, max: 500)
   * @param offset Number of fills to skip (default: 0)
   * @param from Optional ISO 8601 timestamp - only return fills created on or after this time
   * @param to Optional ISO 8601 timestamp - only return fills created on or before this time
   */
  async listFills(
    limit: number = 50,
    offset: number = 0,
    from?: string,
    to?: string
  ): Promise<PaginatedFillsResponse> {
    let url = `/api/fills?limit=${limit}&offset=${offset}`;
    if (from) url += `&from=${encodeURIComponent(from)}`;
    if (to) url += `&to=${encodeURIComponent(to)}`;
    
    const response = await apiFetch<{ fills: Fill[]; has_more: boolean }>(url);
    return {
      fills: response.fills,
      hasMore: response.has_more,
    };
  },

  /**
   * List all fills with optional date filtering
   * @param from Optional ISO 8601 timestamp - only return fills created on or after this time
   * @param to Optional ISO 8601 timestamp - only return fills created on or before this time
   */
  async listAllFills(from?: string, to?: string): Promise<Fill[]> {
    const allFills: Fill[] = [];
    let offset = 0;
    const limit = 500; // Max per request
    let hasMore = true;

    while (hasMore) {
      const response = await this.listFills(limit, offset, from, to);
      allFills.push(...response.fills);
      hasMore = response.hasMore;
      offset += limit;
    }

    return allFills;
  },
};

/**
 * Funding API
 */
export const FundingApi = {
  /**
   * Get current funding rates for all markets
   */
  async getFundingRates(): Promise<FundingRate[]> {
    const response = await apiFetch<FundingRatesResponse>("/api/funding/rates");
    return response.rates;
  },

  /**
   * List funding payments for authenticated user
   */
  async listPayments(): Promise<FundingPayment[]> {
    const response = await apiFetch<FundingPaymentsResponse>(
      `/api/funding/payments`
    );
    return response.payments;
  },
};

/**
 * Market Stats API
 */
export const MarketStatsApi = {
  /**
   * Get market statistics including open interest and funding rates
   */
  async getStats(): Promise<MarketStats[]> {
    const response = await apiFetch<MarketStatsResponse>("/api/markets/stats");
    return response.stats;
  },
};

/**
 * Deposits API
 */
export const DepositsApi = {
  /**
   * Get or create deposit address for authenticated user
   * @param chain - Blockchain to get deposit address for (default: "solana")
   */
  async getDepositAddress(chain: string = "solana"): Promise<DepositAddressResponse> {
    return apiFetch<DepositAddressResponse>(`/api/deposit-address?chain=${chain}`);
  },

  /**
   * List deposit history for authenticated user
   */
  async listDeposits(): Promise<Deposit[]> {
    const response = await apiFetch<DepositListResponse>(`/api/deposits`);
    return response.deposits;
  },
};

/**
 * Faucet API (Devnet only)
 */
export const FaucetApi = {
  /**
   * Request test USDC from the demo faucet (for authenticated user)
   */
  async requestFunds(): Promise<FaucetResponse> {
    return apiFetch<FaucetResponse>("/api/faucet", {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
};

/**
 * Withdrawals API
 */
export const WithdrawalsApi = {
  /**
   * Create a new withdrawal request for authenticated user
   * @param destinationAddress - Solana address to withdraw to
   * @param amount - Amount to withdraw
   * @param totpCode - Optional 2FA code (required if user has 2FA enabled)
   * @param tokenSymbol - Token to withdraw (USDC or USDT). Defaults to USDC.
   */
  async createWithdrawal(
    destinationAddress: string,
    amount: number,
    totpCode?: string,
    tokenSymbol: string = "USDC"
  ): Promise<CreateWithdrawalResponse> {
    const request: CreateWithdrawalRequest = {
      destination_address: destinationAddress,
      amount: amount.toString(),
      totp_code: totpCode,
      token_symbol: tokenSymbol,
    };
    return apiFetch<CreateWithdrawalResponse>("/api/withdrawals", {
      method: "POST",
      body: JSON.stringify(request),
    });
  },

  /**
   * List withdrawal history for authenticated user
   */
  async listWithdrawals(): Promise<Withdrawal[]> {
    const response = await apiFetch<WithdrawalListResponse>(`/api/withdrawals`);
    return response.withdrawals;
  },

  /**
   * Cancel a pending withdrawal (only works for status "pending")
   * Returns funds to available balance
   */
  async cancelWithdrawal(id: string): Promise<{ success: boolean; message: string }> {
    return apiFetch<{ success: boolean; message: string }>(`/api/withdrawals/${id}/cancel`, {
      method: "POST",
    });
  },
};

/**
 * Saved Withdrawal Addresses API
 */
export const WithdrawalAddressesApi = {
  /**
   * List saved withdrawal addresses for authenticated user
   */
  async listAddresses(): Promise<SavedWithdrawalAddress[]> {
    const response = await apiFetch<SavedWithdrawalAddressesResponse>(
      `/api/user/withdrawal-addresses`
    );
    return response.addresses;
  },

  /**
   * Save a new withdrawal address for authenticated user
   */
  async saveAddress(
    address: string,
    label: string
  ): Promise<SavedWithdrawalAddress> {
    const response = await apiFetch<SaveWithdrawalAddressResponse>(
      "/api/user/withdrawal-addresses",
      {
        method: "POST",
        body: JSON.stringify({ address, label }),
      }
    );
    return response.address;
  },

  /**
   * Delete a saved withdrawal address
   */
  async deleteAddress(addressId: string): Promise<void> {
    await apiFetch(`/api/user/withdrawal-addresses/${addressId}`, {
      method: "DELETE",
    });
  },
};

/**
 * User/Wallet API - User preferences and settings
 */
export const UserApi = {
  // exportWallet method removed - Privy users should use Privy's built-in exportWallet
  // Exporting deposit addresses is a security risk (users could double-spend)

  /**
   * Get authenticated user's favorite markets
   */
  async getFavoriteMarkets(): Promise<string[]> {
    const response = await apiFetch<FavoriteMarketsResponse>(
      `/api/favorite-markets`
    );
    return response.favorite_markets;
  },

  /**
   * Update authenticated user's favorite markets
   */
  async updateFavoriteMarkets(
    favorites: string[]
  ): Promise<string[]> {
    const response = await apiFetch<FavoriteMarketsResponse>(
      `/api/favorite-markets`,
      {
        method: "PUT",
        body: JSON.stringify({ favorite_markets: favorites }),
      }
    );
    return response.favorite_markets;
  },
};

/**
 * Two-Factor Authentication API
 */
export const TwoFactorApi = {
  /**
   * Initialize 2FA setup - returns secret and QR code URI
   */
  async initSetup(): Promise<TwoFactorInitResponse> {
    return apiFetch<TwoFactorInitResponse>("/api/2fa/init", {
      method: "POST",
    });
  },

  /**
   * Verify initial TOTP code and enable 2FA
   * Returns backup codes on success (shown only once)
   */
  async verifySetup(code: string): Promise<TwoFactorVerifySetupResponse> {
    return apiFetch<TwoFactorVerifySetupResponse>("/api/2fa/verify-setup", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },

  /**
   * Disable 2FA (requires current TOTP code)
   */
  async disable(code: string): Promise<TwoFactorDisableResponse> {
    return apiFetch<TwoFactorDisableResponse>("/api/2fa/disable", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },

  /**
   * Get 2FA status for authenticated user
   */
  async getStatus(): Promise<TwoFactorStatusResponse> {
    return apiFetch<TwoFactorStatusResponse>("/api/2fa/status");
  },

  /**
   * Regenerate backup codes (requires current TOTP code)
   * Invalidates all previous backup codes
   */
  async regenerateBackupCodes(code: string): Promise<TwoFactorRegenerateBackupResponse> {
    return apiFetch<TwoFactorRegenerateBackupResponse>("/api/2fa/backup-codes", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },
};

/**
 * Config API - System configuration endpoints
 */
export const ConfigApi = {
  /**
   * Get current trading fee rates
   */
  async getFees(): Promise<FeesConfig> {
    return apiFetch<FeesConfig>("/api/config/fees");
  },
};

// ============ Performance Lab Pro™ Performance Review Types ============

/** Per-market performance breakdown */
export interface MarketPerformance {
  market: string;
  trades: number;
  win_rate: number;
  pnl: number;
  volume: number;
}

/** Trading metrics for performance review */
export interface TradingMetrics {
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  break_even_trades: number;
  win_rate: number;
  total_pnl: number;
  total_net_pnl: number;
  total_fees: number;
  total_volume: number;
  average_profit: number;
  average_loss: number;
  average_trade: number;
  largest_win: number;
  largest_loss: number;
  profit_factor: number;
  expectancy: number;
  risk_reward_ratio: number;
  max_drawdown: number;
  max_drawdown_percent: number;
  recovery_factor: number;
  sharpe_ratio: number | null;
  sortino_ratio: number | null;
  current_streak: number;
  current_streak_type: string;
  longest_win_streak: number;
  longest_loss_streak: number;
  avg_trade_duration_hours: number;
  trading_days: number;
  trades_per_day: number;
  best_hour: number | null;
  worst_hour: number | null;
  best_day: string | null;
  worst_day: string | null;
  long_trades: number;
  short_trades: number;
  long_win_rate: number;
  short_win_rate: number;
  size_consistency_score: number;
  overtrading_score: number;
  // NEW: Market breakdown
  market_breakdown: MarketPerformance[];
  // NEW: Leverage analysis
  avg_leverage_used: number;
  max_leverage_used: number;
  // NEW: Order type breakdown
  market_orders: number;
  limit_orders: number;
  market_order_win_rate: number;
  limit_order_win_rate: number;
  // NEW: Funding impact
  total_funding_paid: number;
  total_funding_received: number;
  net_funding: number;
  // NEW: TP/SL usage
  trades_with_tp: number;
  trades_with_sl: number;
  tp_hit_rate: number;
  sl_hit_rate: number;
}

/** AI-generated report content (coaching-style) */
export interface AIReport {
  executive_summary: string;
  key_strengths: string[];
  areas_to_improve: string[];
  recommendations: string[];
  improvements?: string[];
  regressions?: string[];
}

/** Eligibility status for generating a review */
export interface EligibilityStatus {
  eligible: boolean;
  reason: string;
  trades_30d: number;
  fees_30d: number;
  volume_30d: number;
  current_month: number;
  current_year: number;
  has_report_this_month: boolean;
  last_report_date: string | null;
}

/** Stored performance review */
export interface PerformanceReview {
  id: string;
  user_id: string;
  period_month: number;
  period_year: number;
  metrics_snapshot: TradingMetrics;
  ai_report: AIReport;
  previous_report_summary: string | null;
  is_first_report: boolean;
  eligibility_trades: number;
  eligibility_fees: number;
  eligibility_volume: number;
  created_at: string;
}

/** Request to generate a new review */
export interface GenerateReviewRequest {
  force?: boolean;
}

/**
 * Performance Lab Pro™ Performance Review API
 */
export const PerformanceReviewApi = {
  /**
   * Check if user is eligible to generate a review this month
   */
  async checkEligibility(): Promise<EligibilityStatus> {
    return apiFetch<EligibilityStatus>("/api/performance-lab/review/eligibility");
  },

  /**
   * Generate a new performance review for current month
   * @param force - Force regeneration even if report exists
   */
  async generateReview(force = false): Promise<PerformanceReview> {
    const request: GenerateReviewRequest = { force };
    return apiFetch<PerformanceReview>("/api/performance-lab/review/generate", {
      method: "POST",
      body: JSON.stringify(request),
    });
  },

  /**
   * Get current month's report if it exists
   */
  async getCurrentReview(): Promise<PerformanceReview | null> {
    return apiFetch<PerformanceReview | null>("/api/performance-lab/review/current");
  },

  /**
   * Get historical performance reviews
   * @param limit - Max number of reviews to return (default: 12)
   */
  async getReviewHistory(limit = 12): Promise<PerformanceReview[]> {
    return apiFetch<PerformanceReview[]>(
      `/api/performance-lab/review/history?limit=${limit}`
    );
  },
};

/**
 * Generic API client for making requests
 */
export const apiClient = {
  async get<T>(endpoint: string): Promise<T> {
    return apiFetch<T>(endpoint);
  },
  async post<T>(endpoint: string, body: unknown): Promise<T> {
    return apiFetch<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};

